import { copy } from "../copy";
import { producerHref } from "../feed";
import type { RankingRow } from "./data";

function rankClasses(rank: number): string {
  if (rank === 1) return "bg-gold text-ground";
  if (rank === 2) return "bg-chrome text-ground";
  if (rank === 3) return "bg-gold/40 text-chrome";
  return "border border-ground-edge bg-ground text-chrome-dim";
}

const th = "pb-4 text-[11px] font-bold uppercase tracking-[0.16em] text-chrome-dim";

// The leaderboard table. The badge is the server's rank, whatever the sort;
// the table scrolls sideways inside its card on narrow screens.
export default function RankTable({ rows }: { rows: RankingRow[] }) {
  const c = copy.ranking;
  return (
    <div data-testid="rank-table-scroll" className="bf-no-scrollbar overflow-x-auto">
      <table className="w-full min-w-[640px] border-collapse">
        <caption className="sr-only">{c.caption}</caption>
        <thead>
          <tr className="border-b border-ground-edge text-left">
            <th scope="col" className={`${th} pr-4`}>
              {c.name}
            </th>
            <th scope="col" className={`${th} px-4 text-right`}>
              {c.entered}
            </th>
            <th scope="col" className={`${th} px-4 text-right`}>
              {c.won}
            </th>
            <th scope="col" className={`${th} pl-4 text-right`}>
              {c.winnings}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={`${row.rank}-${row.username}`}
              data-testid="rank-row"
              className="border-b border-ground-edge/60 transition-colors hover:bg-ground"
            >
              <td className="py-4 pr-4">
                <div className="flex items-center gap-3">
                  <span
                    data-testid="rank-badge"
                    className={`bf-tabular flex h-7 w-9 shrink-0 items-center justify-center rounded-md text-xs font-bold ${rankClasses(row.rank)}`}
                  >
                    #{row.rank}
                  </span>
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border border-ground-edge bg-ground">
                    {row.avatar_url ? (
                      <img
                        src={row.avatar_url}
                        alt={c.avatar(row.username)}
                        loading="lazy"
                        className="h-full w-full object-cover object-top"
                      />
                    ) : (
                      <span aria-hidden className="font-headline text-lg uppercase text-chrome-dim">
                        {row.username.trim().charAt(0) || "?"}
                      </span>
                    )}
                  </span>
                  <a
                    data-testid="rank-username"
                    href={producerHref(row.username)}
                    className="bf-focusable text-sm font-bold text-chrome hover:text-gold hover:underline"
                  >
                    {row.username}
                  </a>
                </div>
              </td>
              <td className="bf-tabular px-4 py-4 text-right text-sm text-chrome">{row.contests_entered}</td>
              <td className="bf-tabular px-4 py-4 text-right text-sm font-bold text-gold">{row.contests_won}</td>
              <td className="bf-tabular pl-4 py-4 text-right text-sm font-bold text-money">
                {row.total_winnings_display}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
